import { CrossClientQueryLogicalQuestionDecomposition } from '../../../models/CrossClient'

export interface PredefinedCrossClientQuery {
  id: string
  category: string
  title?: string
  description: string
  icon: string
  decomposition: CrossClientQueryLogicalQuestionDecomposition
}

export const PREDEFINED_CROSS_CLIENT_QUERIES: Record<string, PredefinedCrossClientQuery[]> = {
  'Wealth Overview': [
    {
      id: 'predefined-query-16',
      category: 'Wealth Overview',
      title: 'Significant Uninvested Cash',
      description: 'Who are my clients with significant uninvested cash?',
      icon: 'bi-cash-stack',
      decomposition: {
        explanation: 'The question asks for clients who have significant uninvested cash.',
        logicalFormula: 'client_has_significant_uninvested_cash',
        atomicClauses: [
          {
            variable: 'client_has_significant_uninvested_cash',
            description: 'The client has significant uninvested cash.',
            questionForm: 'Does the client have significant uninvested cash?',
          },
        ],
      },
    },
    {
      id: 'predefined-query-18',
      category: 'Wealth Overview',
      title: 'High AUM or Net Worth Clients',
      description: 'Select my clients with over 500k AUM or over 1.5 million Net Worth',
      icon: 'bi-graph-up-arrow',
      decomposition: {
        explanation: 'The question asks for clients with high assets under management or high net worth.',
        logicalFormula: 'client_aum_over_500k OR client_net_worth_over_1_5_million',
        atomicClauses: [
          {
            variable: 'client_aum_over_500k',
            description: 'The client has assets under management over 500,000.',
            questionForm: 'Does the client have over 500,000 in assets under management?',
          },
          {
            variable: 'client_net_worth_over_1_5_million',
            description: 'The client has a net worth over 1.5 million.',
            questionForm: 'Does the client have a net worth over 1.5 million?',
          },
        ],
      },
    },
    {
      id: 'predefined-query-5',
      category: 'Wealth Overview',
      title: 'Self-Employed Without Retirement Accounts',
      description: 'Show all clients who are self-employed but have no SEP IRA or Solo 401(k)',
      icon: 'bi-briefcase',
      decomposition: {
        explanation: 'The question asks for self-employed clients lacking specific retirement accounts.',
        logicalFormula: 'client_is_self_employed AND (client_has_no_sep_ira OR client_has_no_solo_401k)',
        atomicClauses: [
          {
            variable: 'client_is_self_employed',
            description: 'The client is self-employed.',
            questionForm: 'Is the client self-employed?',
          },
          {
            variable: 'client_has_no_sep_ira',
            description: "The client doesn't have a SEP IRA.",
            questionForm: 'Does the client lack a SEP IRA?',
          },
          {
            variable: 'client_has_no_solo_401k',
            description: "The client doesn't have a Solo 401(k).",
            questionForm: 'Does the client lack a Solo 401(k)?',
          },
        ],
      },
    },
    {
      id: 'predefined-query-10',
      category: 'Wealth Overview',
      title: 'Approaching Retirement Without Strategy',
      description: 'Show all clients approaching retirement within 10 years but without a retirement income strategy',
      icon: 'bi-hourglass-split',
      decomposition: {
        explanation: 'The question asks for clients nearing retirement without a retirement income strategy.',
        logicalFormula: 'client_approaching_retirement_within_10_years AND client_has_no_retirement_income_strategy',
        atomicClauses: [
          {
            variable: 'client_approaching_retirement_within_10_years',
            description: 'The client is approaching retirement within 10 years.',
            questionForm: 'Is the client approaching retirement within 10 years?',
          },
          {
            variable: 'client_has_no_retirement_income_strategy',
            description: "The client doesn't have a retirement income strategy.",
            questionForm: 'Does the client lack a retirement income strategy?',
          },
        ],
      },
    },
  ],
  'Family Planning': [
    {
      id: 'predefined-query-6',
      category: 'Family Planning',
      title: 'Children Without Education Savings',
      description: 'List all my clients with children but without an education savings plan',
      icon: 'bi-mortarboard',
      decomposition: {
        explanation: 'The question asks for clients with children who lack education savings plans.',
        logicalFormula: 'client_has_children AND client_has_no_education_savings_plan',
        atomicClauses: [
          {
            variable: 'client_has_children',
            description: 'The client has children.',
            questionForm: 'Does the client have children?',
          },
          {
            variable: 'client_has_no_education_savings_plan',
            description: "The client doesn't have an education savings plan.",
            questionForm: 'Does the client lack an education savings plan?',
          },
        ],
      },
    },
    {
      id: 'predefined-query-22',
      category: 'Family Planning',
      title: 'Child to College in 2 Years',
      description: 'Who among my clients is sending a child to college in the next 2 years',
      icon: 'bi-backpack',
      decomposition: {
        explanation: 'The question asks for clients sending a child to college soon.',
        logicalFormula: 'client_sending_child_to_college_within_2_years',
        atomicClauses: [
          {
            variable: 'client_sending_child_to_college_within_2_years',
            description: 'The client is sending a child to college within the next 2 years.',
            questionForm: 'Is the client sending a child to college within the next 2 years?',
          },
        ],
      },
    },
    {
      id: 'predefined-query-9',
      category: 'Family Planning',
      title: 'Dependents Without Will or Estate Plan',
      description: 'List all clients with dependents but no will or estate plan',
      icon: 'bi-file-earmark-text',
      decomposition: {
        explanation: 'The question asks for clients with dependents who lack estate planning documents.',
        logicalFormula: 'client_has_dependents AND (client_has_no_will OR client_has_no_estate_plan)',
        atomicClauses: [
          {
            variable: 'client_has_dependents',
            description: 'The client has dependents.',
            questionForm: 'Does the client have dependents?',
          },
          {
            variable: 'client_has_no_will',
            description: "The client doesn't have a will.",
            questionForm: 'Does the client lack a will?',
          },
          {
            variable: 'client_has_no_estate_plan',
            description: "The client doesn't have an estate plan.",
            questionForm: 'Does the client lack an estate plan?',
          },
        ],
      },
    },
  ],
  'Risk Protection': [
    {
      id: 'predefined-query-8',
      category: 'Risk Protection',
      title: 'Married Without Life Insurance',
      description: 'Show all my clients who are married but do not have a life insurance policy',
      icon: 'bi-shield-check',
      decomposition: {
        explanation: 'The question asks for married clients lacking life insurance.',
        logicalFormula: 'client_is_married AND client_has_no_life_insurance_policy',
        atomicClauses: [
          {
            variable: 'client_is_married',
            description: 'The client is married.',
            questionForm: 'Is the client married?',
          },
          {
            variable: 'client_has_no_life_insurance_policy',
            description: "The client doesn't have a life insurance policy.",
            questionForm: 'Does the client lack a life insurance policy?',
          },
        ],
      },
    },
    {
      id: 'predefined-query-11',
      category: 'Risk Protection',
      title: 'Homeowners Without Mortgage Protection',
      description: 'List clients who own a home but do not have mortgage protection insurance',
      icon: 'bi-house-check',
      decomposition: {
        explanation: 'The question asks for homeowners without mortgage protection insurance.',
        logicalFormula: 'client_owns_home AND client_has_no_mortgage_protection_insurance',
        atomicClauses: [
          {
            variable: 'client_owns_home',
            description: 'The client owns a home.',
            questionForm: 'Does the client own a home?',
          },
          {
            variable: 'client_has_no_mortgage_protection_insurance',
            description: "The client doesn't have a mortgage protection insurance.",
            questionForm: 'Does the client lack mortgage protection insurance?',
          },
        ],
      },
    },
    {
      id: 'predefined-query-15',
      category: 'Risk Protection',
      title: 'Rental Properties Without Risk Analysis',
      description: 'List all clients with rental properties but no property risk analysis in place',
      icon: 'bi-building',
      decomposition: {
        explanation: 'The question asks for rental property owners without risk analysis.',
        logicalFormula: 'client_owns_rental_property AND client_has_no_property_risk_analysis',
        atomicClauses: [
          {
            variable: 'client_owns_rental_property',
            description: 'The client owns rental property.',
            questionForm: 'Does the client own rental property?',
          },
          {
            variable: 'client_has_no_property_risk_analysis',
            description: "The client doesn't have a property risk analysis in place.",
            questionForm: 'Does the client lack a property risk analysis?',
          },
        ],
      },
    },
  ],
  'Life Events': [
    {
      id: 'predefined-query-4',
      category: 'Life Events',
      title: 'Birthday or Anniversary This Month',
      description: 'Who has a birthday or wedding anniversary this month?',
      icon: 'bi-calendar-heart',
      decomposition: {
        explanation: 'The question asks for clients with a birthday or wedding anniversary this month.',
        logicalFormula: 'client_has_birthday_this_month OR client_has_wedding_anniversary_this_month',
        atomicClauses: [
          {
            variable: 'client_has_birthday_this_month',
            description: 'The client has a birthday this month.',
            questionForm: 'Does the client have a birthday this month?',
          },
          {
            variable: 'client_has_wedding_anniversary_this_month',
            description: 'The client has a wedding anniversary this month.',
            questionForm: 'Does the client have a wedding anniversary this month?',
          },
        ],
      },
    },
    {
      id: 'predefined-query-19',
      category: 'Life Events',
      title: 'Recently Married',
      description: 'Who are my clients who got married this year',
      icon: 'bi-heart',
      decomposition: {
        explanation: 'The question asks for clients who got married this year.',
        logicalFormula: 'client_got_married_this_year',
        atomicClauses: [
          {
            variable: 'client_got_married_this_year',
            description: 'The client got married this year.',
            questionForm: 'Did the client get married this year?',
          },
        ],
      },
    },
    {
      id: 'predefined-query-13',
      category: 'Life Events',
      title: 'Divorced or Widowed Without Updated Beneficiaries',
      description: 'Show me all clients who divorced or were widowed in the past year and have not updated their beneficiary designations',
      icon: 'bi-person-exclamation',
      decomposition: {
        explanation: 'The question asks for recently divorced or widowed clients with outdated beneficiaries.',
        logicalFormula: 'client_divorced_or_widowed_past_year AND client_not_updated_beneficiary_designations',
        atomicClauses: [
          {
            variable: 'client_divorced_or_widowed_past_year',
            description: 'The client divorced or was widowed in the past year.',
            questionForm: 'Did the client divorce or become widowed in the past year?',
          },
          {
            variable: 'client_not_updated_beneficiary_designations',
            description: "The client hasn't updated beneficiary designations.",
            questionForm: 'Has the client skipped updating their beneficiary designations?',
          },
        ],
      },
    },
    {
      id: 'predefined-query-14',
      category: 'Life Events',
      title: 'Job Change or Promotion',
      description: 'List all my clients who changed jobs or got promoted recently',
      icon: 'bi-arrow-up-circle',
      decomposition: {
        explanation: 'The question asks for clients with recent job changes or promotions.',
        logicalFormula: 'client_changed_jobs_recently OR client_got_promoted_recently',
        atomicClauses: [
          {
            variable: 'client_changed_jobs_recently',
            description: 'The client changed jobs recently.',
            questionForm: 'Did the client change jobs recently?',
          },
          {
            variable: 'client_got_promoted_recently',
            description: 'The client got promoted recently.',
            questionForm: 'Did the client get promoted recently?',
          },
        ],
      },
    },
    {
      id: 'predefined-query-23',
      category: 'Life Events',
      title: 'Recently Bought a Home',
      description: 'Who are my clients who recently bought a home',
      icon: 'bi-house-door',
      decomposition: {
        explanation: 'The question asks for clients who recently bought a home.',
        logicalFormula: 'client_recently_bought_home',
        atomicClauses: [
          {
            variable: 'client_recently_bought_home',
            description: 'The client recently bought a home.',
            questionForm: 'Did the client recently buy a home?',
          },
        ],
      },
    },
    {
      id: 'predefined-query-20',
      category: 'Life Events',
      title: 'Planning to Relocate or Recently Moved',
      description: 'Show all clients who are planning to relocate or recently moved',
      icon: 'bi-truck',
      decomposition: {
        explanation: 'The question asks for clients planning relocation or who recently moved.',
        logicalFormula: 'client_plans_to_relocate OR client_recently_moved',
        atomicClauses: [
          {
            variable: 'client_plans_to_relocate',
            description: 'The client is planning to relocate.',
            questionForm: 'Is the client planning to relocate?',
          },
          {
            variable: 'client_recently_moved',
            description: 'The client recently moved.',
            questionForm: 'Did the client recently move?',
          },
        ],
      },
    },
  ],
  'Lifestyle & Goals': [
    {
      id: 'predefined-query-1',
      category: 'Lifestyle & Goals',
      title: 'Golf or Tennis Players',
      description: 'Who are my clients who play golf or tennis?',
      icon: 'bi-dribbble',
      decomposition: {
        explanation: 'The question asks for clients who play golf or tennis.',
        logicalFormula: 'client_plays_golf OR client_plays_tennis',
        atomicClauses: [
          {
            variable: 'client_plays_golf',
            description: 'The client plays golf.',
            questionForm: 'Does the client play golf?',
          },
          {
            variable: 'client_plays_tennis',
            description: 'The client plays tennis.',
            questionForm: 'Does the client play tennis?',
          },
        ],
      },
    },
    {
      id: 'predefined-query-2',
      category: 'Lifestyle & Goals',
      title: 'Football Fans',
      description: 'Which of my clients are football fans or plan to watch the Super Bowl?',
      icon: 'bi-trophy',
      decomposition: {
        explanation: 'The question asks for clients who are football fans or planning to watch the Super Bowl.',
        logicalFormula: 'client_is_football_fan OR client_plans_to_watch_super_bowl',
        atomicClauses: [
          {
            variable: 'client_is_football_fan',
            description: 'The client is a football fan.',
            questionForm: 'Is the client a football fan?',
          },
          {
            variable: 'client_plans_to_watch_super_bowl',
            description: 'The client plans to watch the Super Bowl.',
            questionForm: 'Does the client plan to watch the Super Bowl?',
          },
        ],
      },
    },
    {
      id: 'predefined-query-3',
      category: 'Lifestyle & Goals',
      title: 'Planning Expensive Vacation',
      description: 'List clients who are planning a long overseas or potentially expensive vacation this year',
      icon: 'bi-airplane',
      decomposition: {
        explanation: 'The question asks for clients planning significant vacations.',
        logicalFormula: 'client_plans_long_overseas_vacation_this_year OR client_plans_expensive_vacation_this_year',
        atomicClauses: [
          {
            variable: 'client_plans_long_overseas_vacation_this_year',
            description: 'The client is planning a long overseas vacation this year.',
            questionForm: 'Is the client planning a long overseas vacation this year?',
          },
          {
            variable: 'client_plans_expensive_vacation_this_year',
            description: 'The client is planning a potentially expensive vacation this year.',
            questionForm: 'Is the client planning a potentially expensive vacation this year?',
          },
        ],
      },
    },
    {
      id: 'predefined-query-17',
      category: 'Lifestyle & Goals',
      title: 'Vacation Home Retirement Goal',
      description: 'Which clients have mentioned a vacation home or property abroad as a retirement goal?',
      icon: 'bi-house-heart',
      decomposition: {
        explanation: 'The question asks for clients with vacation home retirement goals.',
        logicalFormula: 'client_retirement_goal_vacation_home_or_property_abroad',
        atomicClauses: [
          {
            variable: 'client_retirement_goal_vacation_home_or_property_abroad',
            description: 'The client has mentioned a vacation home or property abroad as a retirement goal.',
            questionForm: 'Has the client mentioned a vacation home or property abroad as a retirement goal?',
          },
        ],
      },
    },
    {
      id: 'predefined-query-21',
      category: 'Lifestyle & Goals',
      title: 'Charitable Activities',
      description: 'Show all of my clients who participate in charity events or charitable activities',
      icon: 'bi-heart-fill',
      decomposition: {
        explanation: 'The question asks for clients involved in charitable activities.',
        logicalFormula: 'client_participates_in_charitable_activities',
        atomicClauses: [
          {
            variable: 'client_participates_in_charitable_activities',
            description: 'The client participates in charity events or charitable activities.',
            questionForm: 'Does the client participate in charity events or charitable activities?',
          },
        ],
      },
    },
    {
      id: 'predefined-query-12',
      category: 'Lifestyle & Goals',
      title: 'Business Owners Without Succession Plan',
      description: "List all clients who own a business or have more than 50% share but doesn't have a succession plan in place",
      icon: 'bi-building-gear',
      decomposition: {
        explanation: 'The question asks for business owners without succession plans.',
        logicalFormula: '(client_owns_business OR client_owns_majority_business_share) AND client_has_no_business_succession_plan',
        atomicClauses: [
          {
            variable: 'client_owns_business',
            description: 'The client owns a business.',
            questionForm: 'Does the client own a business?',
          },
          {
            variable: 'client_owns_majority_business_share',
            description: 'The client owns more than 50% of a business.',
            questionForm: 'Does the client own more than 50% of a business?',
          },
          {
            variable: 'client_has_no_business_succession_plan',
            description: "The client doesn't have a business succession plan.",
            questionForm: 'Does the client lack a business succession plan?',
          },
        ],
      },
    },
  ],
}

export const FEATURED_CROSS_CLIENT_QUERIES: PredefinedCrossClientQuery[] = [
  PREDEFINED_CROSS_CLIENT_QUERIES['Wealth Overview'][0],
  PREDEFINED_CROSS_CLIENT_QUERIES['Life Events'][0],
  PREDEFINED_CROSS_CLIENT_QUERIES['Lifestyle & Goals'][0],
]

export const CROSS_CLIENT_PREDEFINED_CATEGORIES = ['Featured', ...Object.keys(PREDEFINED_CROSS_CLIENT_QUERIES)]

export type CrossClientPredefinedCategory = (typeof CROSS_CLIENT_PREDEFINED_CATEGORIES)[number]

export const CROSS_CLIENT_PREDEFINED_ICONS: Record<CrossClientPredefinedCategory, string> = {
  Featured: 'bi-star',
}
